// LogsList.tsx
import useFetch from "../../../hooks/useFetch";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  InputRef,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import { TFilterModel } from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { dateFormat, defaultFilterParams } from "../../../utils/constants";
import utils from "../../../utils";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import {
  CloseOutlined,
  FilterFilled,
  FilterOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ExportToCSV from "../../../components/export-to-csv/export-to-csv";
import Highlighter from "react-highlight-words";
import { FilterDropdownProps } from "antd/es/table/interface";

export default function LogsList() {
  // initialization of hooks
  const { post } = useFetch(); // custom hook used to call the apis
  const [isLoading, setIsLoading] = useState(true); // to manage the loader
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams); // object to fetch the data in list
  const { onShowToast } = useToast(); // custom hook used to show toast
  const searchInput = useRef<InputRef>(null);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const handleReset = (clearFilters: FilterDropdownProps["clearFilters"]) => {
    if (clearFilters) {
      clearFilters();
    }
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: any): any => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e: any) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{  color: "var(--bs-search-style)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e: any) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: any
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const initialColumns: any[] = [
    {
      title: "Log Date",
      dataIndex: "createdOn",
      key: "createdOn",
      width: "15%",
      filterSearch: true,
      sorter: (a: any, b: any) =>
        String(a.createdOn)?.localeCompare(String(b.createdOn)),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, dateFormat);
      },
    },
    {
      title: "Action Description",
      dataIndex: "auditMessage",
      key: "auditMessage",
      ...getColumnSearchProps("auditMessage"),
      width: "50%",
      filterSearch: true,
      sorter: (a: any, b: any) =>
        a.auditMessage.localeCompare(b.auditMessage),
      onFilter: (value: string | number | boolean, record: any) =>
        record.auditMessage.includes(value as string),
    },
    {
      title: "Action Taken By",
      dataIndex: "actionByUserName",
      key: "actionByUserName",
      width: "15%",
      filterSearch: true,
      sorter: (a: any, b: any) =>
        a?.actionByUserName.localeCompare(b?.actionByUserName),
      onFilter: (value: string | number | boolean, record: any) =>
        record.actionByUserName.includes(value as string),
    },
    {
      title: "Organization",
      dataIndex: "orgName",
      key: "orgName",
      width: "15%",
      filterSearch: true,
      sorter: (a: any, b: any) => a?.orgName.localeCompare(b?.orgName),
      onFilter: (value: string | number | boolean, record: any) =>
        record.orgName.includes(value as string),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns);

  useEffect(() => {
    // fetchData();
  }, []);

  const filteredData : any=[]
    // data.filter((item) =>
    // Object.values(item).some((val) =>
    //   val?.toString().toLowerCase().includes(globalsearchText.toLowerCase())
    // )
  

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  // const fetchData = async () => {
  //   try {
  //     setIsLoading(true);
  //     const requestPayload = {
  //       data: {
  //         currentPage: 1,
  //         pageSize: PazeSize,
  //       },
  //     };

  //     const response = await post(GetAudianyList, requestPayload);
  //     const { data, dataResponse, filterModel } = response;
  //     const { returnCode, description } = dataResponse;
  //     if (returnCode === eResultCode.SUCCESS) {
  //       const formattedData = data.map((item: any, index: number) => ({
  //         key: (item.id ?? "").toString(),
  //         srNo: index + 1,
  //         createdOn: item.createdOn,
  //         auditMessage: item.auditMessage,
  //         actionByUserName: item.actionByUserName,
  //         orgName: item.orgName,
  //       }));
  //       setData(formattedData);
  //       setFilterParams({
  //         ...(filterModel as TFilterModel),
  //         totalRows: data.length,
  //       });
  //       setIsLoading(false);

  //       const auditMessageFilters = Array.from(
  //         new Set(data.map((item: any) => item.auditMessage))
  //       ).map((auditMessage) => ({ text: auditMessage, value: auditMessage }));
  //       const actionByUserNameFilters = Array.from(
  //         new Set(data.map((item: any) => item.actionByUserName))
  //       ).map((actionByUserName) => ({
  //         text: actionByUserName,
  //         value: actionByUserName,
  //       }));
  //       const orgNameFilters = Array.from(
  //         new Set(data.map((item: any) => item.orgName))
  //       ).map((orgName) => ({ text: orgName, value: orgName }));

  //       setColumns((prevColumns) =>
  //         prevColumns.map((col) => {
  //           if (col.key === "auditMessage") {
  //             return { ...col, filters: auditMessageFilters };
  //           }
  //           if (col.key === "actionByUserName") {
  //             return { ...col, filters: actionByUserNameFilters };
  //           }
  //           if (col.key === "orgName") {
  //             return { ...col, filters: orgNameFilters };
  //           }
  //           return col;
  //         })
  //       );
  //     } else {
  //       onShowToast({
  //         type: ToastType.error,
  //         title: <CloseOutlined />,
  //         position: ToastOpen.centerTop,
  //         content: description,
  //       });
  //       setIsLoading(false);
  //     }
  //   } catch (error) {
  //     console.error("Error:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  // const exportData = data.map((user) => ({
  //   ...user,
  // }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        // icon={<FaObjectGroup />}
        title={`${filterParams?.totalRows} Total Audit Logs`}
        size="large"
      >
        {/* <Space>
          <Input
            placeholder="Filter by keywords"
            value={globalsearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`logs_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space> */}
      </PageHeader>
      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
